import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-xl border border-border bg-white px-4 py-3.5 text-sm text-ink placeholder:text-muted-foreground/70 outline-none transition-shadow duration-200 focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-background";

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "",
    service: "Heat Pumps", message: "", preferred_contact: "Phone",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="rounded-3xl bg-white border border-border p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
        <h3 className="mt-5 font-serif text-3xl text-navy">Request received</h3>
        <p className="mt-3 text-muted-foreground max-w-md mx-auto">
          Thank you, {form.name.split(" ")[0]}. A member of our family-owned team will reach out soon to talk through your home's comfort.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="rounded-3xl bg-white border border-border p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Name *</label>
          <input data-testid="quote-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Homeowner" />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Phone *</label>
          <input data-testid="quote-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(508) 555-0142" />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Email *</label>
          <input data-testid="quote-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Address</label>
          <input data-testid="quote-address" className={field} value={form.address} onChange={set("address")} placeholder="Town, MA" />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Service Needed</label>
          <select data-testid="quote-service" className={field} value={form.service} onChange={set("service")}>
            {SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
            <option>Not sure yet</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-navy mb-1.5 block">Preferred Contact</label>
          <select data-testid="quote-preferred" className={field} value={form.preferred_contact} onChange={set("preferred_contact")}>
            <option>Phone</option>
            <option>Email</option>
            <option>Text</option>
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs font-semibold text-navy mb-1.5 block">Message</label>
        <textarea data-testid="quote-message" rows={4} className={field} value={form.message} onChange={set("message")} placeholder="Tell us a little about your home and what you're looking for..." />
      </div>
      <button
        type="submit"
        data-testid="quote-submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy/85 disabled:opacity-60"
      >
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : "Request My Quote"}
      </button>
      <p className="mt-3 text-center text-xs text-muted-foreground">No pressure, ever. We'll only reach out about your request.</p>
    </form>
  );
};
